import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { usePaycodeForm } from 'hooks/paycodes/usePaycodeForm';
import { useTranslation } from 'react-i18next';
import type { PaycodeCreate } from 'repository/timekeeper/@types/PaycodeModel';
import { nameValidation } from 'utils/Validations';

interface Props {
    form: ReturnType<typeof usePaycodeForm>;
    mode: 'add' | 'edit' | 'delete' | 'info';
}

export const PaycodeForm = ({ form, mode }: Props) => {
    const { t } = useTranslation();
    const { existCodeNumber, existName, errorInCodeNumber, form: formState, rows, currentPaycodeId } = form;
    return (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    id="name"
                    label={t('settings.paycodes.table.modals.add.name')}
                    variant="outlined"
                    value={formState.name}
                    autoComplete="off"
                    onChange={(e) => {
                        const newName = e.target.value;
                        if (nameValidation(newName)) {
                            form.update('name', newName);
                        }
                    }}
                    fullWidth
                    required
                    slotProps={{
                        htmlInput: { maxLength: 50 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${formState.name.length}/50`}</InputAdornment>,
                        },
                    }}
                    error={existName(formState.name)}
                />
                <FormHelperText
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.5,
                        px: 1.75,
                    }}
                >
                    <span>{existName(formState.name) ? t('settings.paycodes.table.modals.add.exist_name') : ''}</span>
                </FormHelperText>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    id="abbreviated_name"
                    label={t('settings.paycodes.table.modals.add.abbreviated_name')}
                    variant="outlined"
                    value={formState.abbreviated_name ?? ''}
                    autoComplete="off"
                    onChange={(e) => form.update('abbreviated_name', e.target.value)}
                    fullWidth
                    slotProps={{
                        htmlInput: { maxLength: 10 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${(formState.abbreviated_name ?? '').length}/10`}</InputAdornment>,
                        },
                    }}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    id="code_number"
                    label={t('settings.paycodes.table.modals.add.code_number')}
                    variant="outlined"
                    value={formState.code_number ?? ''}
                    autoComplete="off"
                    onChange={(e) => {
                        const newCodeNumber = e.target.value;
                        if (errorInCodeNumber(newCodeNumber)) {
                            form.update('code_number', newCodeNumber);
                            existCodeNumber(newCodeNumber);
                        }
                    }}
                    error={existCodeNumber(formState.code_number)}
                    fullWidth
                    slotProps={{
                        htmlInput: { maxLength: 6 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${(formState.code_number ?? '').length}/6`}</InputAdornment>,
                        },
                    }}
                />
                <FormHelperText
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.5,
                        px: 1.75,
                    }}
                >
                    {/* <span>{errorInCodeNumber(codeNumber) ? t('settings.paycodes.table.modals.add.code_number_error') : ''}</span> */}
                    <span>{existCodeNumber(formState.code_number) ? t('settings.paycodes.table.modals.add.exist_code_number') : ''}</span>
                </FormHelperText>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    id="description"
                    label={t('settings.paycodes.table.modals.add.description')}
                    variant="outlined"
                    multiline
                    rows={2}
                    value={formState.description ?? ''}
                    autoComplete="off"
                    onChange={(e) => {
                        form.update('description', e.target.value);
                    }}
                    fullWidth
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${(formState.description ?? '').length}/255`}</InputAdornment>,
                        },
                    }}
                />
            </Grid>



            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <FormControl fullWidth required disabled={mode === 'edit'}>
                    <InputLabel id="type">{t('settings.paycodes.table.modals.add.type.title')}</InputLabel>
                    <Select
                        labelId="type"
                        id="type"
                        value={formState.type}
                        label={t('settings.paycodes.table.modals.add.type.title')}
                        onChange={(event) => {
                            const selectedType = event.target.value as PaycodeCreate['type'];
                            form.update('type', selectedType);
                            form.update('timesheet_resolved_exception', false);
                            form.update('requires_approval', false);
                            form.update('unjustified_exception', false);
                            form.update('justified_exception', false);
                            form.update('asscociate_paycode', false);
                            form.update('paycode_asociate', null);
                            form.update('always_process_duration_separate_shift', false);

                            if (selectedType === 'duration') {
                                form.update('unit', 'hours');
                                form.update('multiplier', 0);
                                form.update('add', 0);
                            } else {
                                form.update('multiplier', 1.0);
                                form.update('add', 0.0);
                            }
                        }}
                    >
                        <MenuItem value={'standard'}>{t('settings.paycodes.table.modals.add.type.standard')}</MenuItem>
                        <MenuItem value={'duration'}>{t('settings.paycodes.table.modals.add.type.duration')}</MenuItem>
                        <MenuItem value={'domino'}>{t('settings.paycodes.table.modals.add.type.domino')}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <FormControl fullWidth disabled={mode === 'edit' || formState.type === 'duration'}>
                    <InputLabel id="unit">{t('settings.paycodes.table.modals.add.unit.title')}</InputLabel>
                    <Select
                        labelId="unit"
                        id="unit"
                        value={formState.unit}
                        label={t('settings.paycodes.table.modals.add.unit.title')}
                        onChange={(event) => {
                            const selectedUnit = event.target.value as PaycodeCreate['unit'];
                            form.update('unit', selectedUnit);
                            form.update('timesheet_resolved_exception', false);
                            form.update('requires_approval', false);
                            form.update('unjustified_exception', false);
                            form.update('justified_exception', false);
                            form.update('asscociate_paycode', false);
                            form.update('paycode_asociate', null);
                            form.update('always_process_duration_separate_shift', false);

                            if (selectedUnit === 'hours') {
                                form.update('multiplier', 1.0);
                                form.update('add', 0.0);
                            } else {
                                form.update('multiplier', 0);
                                form.update('add', 0);
                            }
                        }}
                    >
                        <MenuItem value={'hours'}>{t('settings.paycodes.table.modals.add.unit.hours')}</MenuItem>
                        <MenuItem value={'days'}>{t('settings.paycodes.table.modals.add.unit.days')}</MenuItem>
                        <MenuItem value={'money'} disabled={formState.type === 'domino'}>
                            {t('settings.paycodes.table.modals.add.unit.money')}
                        </MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formState.timesheet_resolved_exception}
                                onChange={(e) => {
                                    form.update('timesheet_resolved_exception', e.target.checked);
                                    form.update('requires_approval', false);
                                    form.update('unjustified_exception', false);
                                    form.update('justified_exception', false);
                                    form.update('always_process_duration_separate_shift', false);
                                }}
                                indeterminate={formState.timesheet_resolved_exception === false && (formState.requires_approval || formState.unjustified_exception || formState.justified_exception)}
                            />
                        }
                        label={t('settings.paycodes.table.modals.add.timesheet_resolve_exception')}
                        disabled={formState.type !== 'duration' || formState.always_process_duration_separate_shift}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                        <FormControlLabel
                            control={<Checkbox checked={formState.requires_approval} onChange={(e) => form.update('requires_approval', e.target.checked)} />}
                            label={t('settings.paycodes.table.modals.add.requires_aproval')}
                            disabled={formState.type === 'standard' || formState.timesheet_resolved_exception === false}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={formState.unjustified_exception} onChange={(e) => form.update('unjustified_exception', e.target.checked)} />}
                            label={t('settings.paycodes.table.modals.add.unjustified_exception')}
                            disabled={formState.type === 'standard' || formState.timesheet_resolved_exception === false}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={formState.justified_exception} onChange={(e) => form.update('justified_exception', e.target.checked)} />}
                            label={t('settings.paycodes.table.modals.add.justified_exception')}
                            disabled={formState.type === 'standard' || formState.timesheet_resolved_exception === false}
                        />
                    </Box>
                </div>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formState.asscociate_paycode}
                            onChange={(e) => {
                                form.update('asscociate_paycode', e.target.checked);

                                if (e.target.checked === false) {
                                    form.update('paycode_asociate', null);
                                }
                            }}
                        />
                    }
                    label={t('settings.paycodes.table.modals.add.asociated_paycode')}
                    disabled={form.form.unit !== 'days'}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <Autocomplete
                    disabled={formState.unit !== 'days' || formState.asscociate_paycode === false}
                    options={rows.filter((paycode) => ((paycode.type === 'standard' && paycode.unit === 'hours') || paycode.type === 'duration') && paycode.id !== currentPaycodeId)}
                    getOptionLabel={(option) => option.name}
                    value={rows.find((p) => p.id === formState.paycode_asociate) ?? null}
                    onChange={(_, selected) => {
                        form.update('paycode_asociate', selected ? selected.id : null);
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label={t('settings.paycodes.table.modals.add.paycode.title')} />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formState.always_process_duration_separate_shift}
                            onChange={(e) => {
                                form.update('always_process_duration_separate_shift', e.target.checked);
                                form.update('timesheet_resolved_exception', false);
                            }}
                        />
                    }
                    label={t('settings.paycodes.table.modals.add.always_process_duration_separate_shift')}
                    disabled={formState.type !== 'duration' || formState.timesheet_resolved_exception}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    id="multiplier"
                    label={t('settings.paycodes.table.modals.add.multiplier')}
                    variant="outlined"
                    value={formState.multiplier}
                    onChange={(e) => {
                        const multiplier = Number(e.target.value);
                        form.update('multiplier', Number.isNaN(multiplier) ? 0 : multiplier);
                    }}
                    fullWidth
                    type="number"
                    slotProps={{ htmlInput: { step: 0.01 } }}
                    disabled={formState.unit !== 'hours' || formState.type === 'duration'}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    id="add"
                    label={t('settings.paycodes.table.modals.add.add')}
                    variant="outlined"
                    value={formState.add}
                    rows={4}
                    onChange={(e) => {
                        const addValue = Number(e.target.value);
                        form.update('add', Number.isNaN(addValue) ? 0 : addValue);
                    }}
                    fullWidth
                    type="number"
                    slotProps={{ htmlInput: { step: 0.01 } }}
                    disabled={formState.unit !== 'hours' || formState.type === 'duration'}
                />
            </Grid>
        </Grid>
    );
};
